import { useAuthStore } from '@/features/auth/useAuthStore.js'
import {
  TENANT_LOGIN_ERROR_CODES,
  TENANT_LOGIN_ERROR_QUERY_KEY,
} from '@/features/auth/utils/clubSelectorTenantLoginError.js'
import {
  LoginTenantAccessError,
  LOGIN_TENANT_NO_ACCESS_CODE,
  ensureTemporaryTenantToken,
  findTenantMobileProfileId,
  invalidateTemporaryTenantToken,
} from '@/features/auth/utils/temporaryTenantSession.js'
import { getTemporaryTokenTenantId } from '@/features/auth/auth.utils.js'
import { LOGOUT_FLOWS } from '@/features/auth/logoutRoute.js'
import { clearActiveClubId, getActiveClubId } from '@/features/router/useActiveClub.js'
import { isSuperAdmin } from '@/features/superAdmin/useIsSuperAdmin.js'

function getRouteActiveClubId(to) {
  const activeClubId = to?.query?.activeClubId
  return Array.isArray(activeClubId) ? activeClubId[0] : activeClubId
}

function getRouteQueryValue(value) {
  return Array.isArray(value) ? value[0] : value
}

function getLoginTenantFailureQueryValue(error) {
  if (error instanceof LoginTenantAccessError && error.errorCode === LOGIN_TENANT_NO_ACCESS_CODE) {
    return TENANT_LOGIN_ERROR_CODES.accessDenied
  }

  return TENANT_LOGIN_ERROR_CODES.failed
}

function buildClubSelectorRedirect(from, to, tenantLoginError) {
  const redirectUrl = getRouteQueryValue(to.query?.redirectUrl || from.query?.redirectUrl)
  const query = {}

  if (redirectUrl) {
    query.redirectUrl = redirectUrl
  }

  if (tenantLoginError) {
    query[TENANT_LOGIN_ERROR_QUERY_KEY] = tenantLoginError
  }

  return {
    path: '/auth/club-selector',
    ...(Object.keys(query).length > 0 ? { query } : {}),
  }
}

/**
 * Route guard for club-context routes (activeClubId). When the selected
 * tenant exposes a mobileProfileId, obtains a temporary tenant token via
 * Authorization/Authentication/LoginTenant before entering the club.
 *
 * Legacy flow (no mobileProfileId) and super-admin impersonation are unchanged.
 */
export async function tenantTokenGuard(to, from, next) {
  const clubId = getRouteActiveClubId(to) || getActiveClubId()
  const authStore = useAuthStore()
  const myDetails = authStore.myDetails

  if (!clubId || !myDetails || isSuperAdmin(myDetails)) {
    next()
    return
  }

  const requiresTemporaryToken = !!findTenantMobileProfileId(myDetails, clubId)

  try {
    await ensureTemporaryTenantToken(myDetails, clubId)
    next()
  } catch (error) {
    if (!requiresTemporaryToken) {
      console.error('[tenantTokenGuard] Failed to obtain temporary tenant token:', error)
      next()
      return
    }

    clearActiveClubId()

    next(buildClubSelectorRedirect(from, to, getLoginTenantFailureQueryValue(error)))
  }
}

/**
 * Global guard: invalidates the temporary tenant token as soon as navigation
 * leaves the tenant it was issued for (club selector, super admin, another
 * club, landing without club context, ...).
 */
export function temporaryTokenCleanupGuard(to) {
  const temporaryTokenTenantId = getTemporaryTokenTenantId()

  if (!temporaryTokenTenantId) {
    return true
  }

  const logoutFlow = getRouteQueryValue(to.query?.logoutFlow)

  // AuthLogoutView owns club-exit invalidation; clearing here first would make it
  // think there is no temporary token and fall through to a full session logout.
  if (to.path === '/auth/logout' && logoutFlow === LOGOUT_FLOWS.exitClub) {
    return true
  }

  if (getRouteActiveClubId(to) !== temporaryTokenTenantId) {
    // Fire-and-forget: navigation must not wait for the BE logout.
    invalidateTemporaryTenantToken()
  }

  return true
}
